import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppsTable, SectionTitle } from "@/components/AppTable";
import { toast } from "sonner";
import { Download, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";

const INSTALL_OPTIONS = [
  { v: "", l: "Any installs" },
  { v: 1000, l: "1K+" }, { v: 10000, l: "10K+" }, { v: 50000, l: "50K+" },
  { v: 100000, l: "100K+" }, { v: 500000, l: "500K+" },
  { v: 1000000, l: "1M+" }, { v: 10000000, l: "10M+" }, { v: 100000000, l: "100M+" },
];

export default function Apps({ preset }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [minRating, setMinRating] = useState("");
  const [maxRating, setMaxRating] = useState(preset?.max_rating || "");
  const [minInstalls, setMinInstalls] = useState("");
  const [maxInstalls, setMaxInstalls] = useState("");
  const [releasedDays, setReleasedDays] = useState("");
  const [firstSeenDays, setFirstSeenDays] = useState(preset?.first_seen_days || "");
  const [meta, setMeta] = useState({ categories: [] });
  const [loading, setLoading] = useState(false);
  const [excludeLeads, setExcludeLeads] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [addingBulk, setAddingBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25, exclude_leads: excludeLeads };
      if (q) params.q = q;
      if (category) params.category = category;
      if (minRating) params.min_rating = minRating;
      if (maxRating) params.max_rating = maxRating;
      if (minInstalls) params.min_installs = minInstalls;
      if (maxInstalls) params.max_installs = maxInstalls;
      if (releasedDays) params.released_days = releasedDays;
      if (firstSeenDays) params.first_seen_days = firstSeenDays;
      const r = await api.get("/apps", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load apps"); }
    finally { setLoading(false); }
  };

  useEffect(() => { api.get("/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [page, category, minRating, maxRating, minInstalls, maxInstalls, releasedDays, firstSeenDays, excludeLeads]);

  const toggleOne = (pkg) => {
    const next = new Set(selected);
    if (next.has(pkg)) next.delete(pkg); else next.add(pkg);
    setSelected(next);
  };
  const toggleAll = (eligible, allSelected) => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(eligible.map(a => a.package_name)));
  };

  const bulkAdd = async () => {
    if (!selected.size) return;
    setAddingBulk(true);
    try {
      const r = await api.post("/apps/bulk-add-leads", { package_names: Array.from(selected) });
      toast.success(`Added ${r.data.count} to leads` + (r.data.skipped?.length ? ` (${r.data.skipped.length} skipped)` : ""));
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add leads");
    } finally { setAddingBulk(false); }
  };

  const doExport = (fmt) => {
    const token = localStorage.getItem("appradar_token") || "";
    const url = `${API_BASE}/export/apps.${fmt}?_=${Date.now()}`;
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `appradar_apps.${fmt}`;
        a.click();
      });
  };

  return (
    <div className="p-8 space-y-6">
      <SectionTitle
        eyebrow="Directory"
        title={preset?.title || "Apps"}
        actions={
          <>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground border border-border px-2 py-2 rounded-sm cursor-pointer hover:bg-accent">
              <input type="checkbox" checked={excludeLeads} data-testid="toggle-exclude-leads"
                     onChange={(e) => { setExcludeLeads(e.target.checked); setPage(1); }} />
              Hide apps already in leads
            </label>
            <button data-testid="btn-bulk-add-leads" disabled={!selected.size || addingBulk} onClick={bulkAdd}
                    className="bg-primary text-primary-foreground px-3 py-2 text-xs font-medium rounded-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
              <Plus size={14} /> {addingBulk ? "Adding…" : selected.size ? `Add ${selected.size} to leads` : "Add to leads"}
            </button>
            <button data-testid="btn-export-csv" onClick={() => doExport("csv")}
              className="border border-border px-3 py-2 text-xs font-medium rounded-sm hover:bg-accent flex items-center gap-1.5">
              <Download size={14} /> CSV
            </button>
            <button data-testid="btn-export-xlsx" onClick={() => doExport("xlsx")}
              className="border border-border px-3 py-2 text-xs font-medium rounded-sm hover:bg-accent flex items-center gap-1.5">
              <Download size={14} /> XLSX
            </button>
          </>
        }
      />

      <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
            className="grid grid-cols-2 md:grid-cols-7 gap-2 border border-border p-3 bg-secondary/30">
        <div className="col-span-2 md:col-span-2 relative">
          <MagnifyingGlass className="absolute left-2.5 top-2.5 text-muted-foreground" size={14} />
          <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name / package / developer"
                 className="w-full border border-input bg-background pl-8 pr-2 py-2 text-sm rounded-sm" />
        </div>
        <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}
                className="border border-input bg-background px-2 py-2 text-sm rounded-sm">
          <option value="">All Categories</option>
          {meta.categories?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-min-installs" value={minInstalls} onChange={(e) => setMinInstalls(e.target.value)}
                className="border border-input bg-background px-2 py-2 text-sm rounded-sm">
          {INSTALL_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l ? "Min " + o.l : "Any installs"}</option>)}
        </select>
        <select data-testid="filter-max-installs" value={maxInstalls} onChange={(e) => setMaxInstalls(e.target.value)}
                className="border border-input bg-background px-2 py-2 text-sm rounded-sm">
          <option value="">Any max</option>
          <option value={10000}>Max 10K</option>
          <option value={100000}>Max 100K</option>
          <option value={500000}>Max 500K</option>
          <option value={1000000}>Max 1M</option>
        </select>
        <select data-testid="filter-max-rating" value={maxRating} onChange={(e) => setMaxRating(e.target.value)}
                className="border border-input bg-background px-2 py-2 text-sm rounded-sm">
          <option value="">Any rating</option>
          <option value="3.0">Rating &lt; 3.0</option>
          <option value="3.5">Rating &lt; 3.5</option>
          <option value="4.0">Rating &lt; 4.0</option>
        </select>
        <select data-testid="filter-released" value={releasedDays} onChange={(e) => setReleasedDays(e.target.value)}
                className="border border-input bg-background px-2 py-2 text-sm rounded-sm">
          <option value="">Any release date</option>
          <option value="30">Released ≤ 30d</option>
          <option value="90">Released ≤ 90d</option>
          <option value="180">Released ≤ 180d</option>
        </select>
      </form>

      <AppsTable apps={items} selectable={true} selectedSet={selected}
                 onToggle={toggleOne} onToggleAll={toggleAll} />

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>{total} apps · page {page}</div>
        <div className="flex gap-2">
          <button data-testid="prev-page" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="border border-border px-2 py-1 rounded-sm disabled:opacity-40">Prev</button>
          <button data-testid="next-page" disabled={items.length < 25} onClick={() => setPage(p => p + 1)}
                  className="border border-border px-2 py-1 rounded-sm disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
